import axios from 'axios';
import { isLoggedIn } from '../auth';

/**
* Gets a list of courses
* @returns {Promise} response - promise of courses to be resolved
*/
export function getCourses(limit, offset) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes?limit=${limit}&offset=${offset}`);
	}
	return Promise.resolve(new Response({
		message: 'Unauthorised'
	}, {
		status: 403, statusText: 'Unauthorised'
	}));
}

export function getHomePageCourses(limit, offset) {
	const status = 1;
	return axios.get(`/v1/classes/filter?status=${status}&limit=${limit}&offset=${offset}`);
}

/**
* Gets a list of filtered courses
* @param {String} - status query type of classes to be filtered
* @returns {Promise} response - promise of filtered courses to be resolved
*/
export function getFilteredCourses(status, careerId) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes/filter?status=${status}&career_id=${careerId}`);
	}
	return Promise.resolve(new Response({
		message: 'Unauthorised'
	}, {
		status: 403, statusText: 'Unauthorised'
	}));
}

/**
 * Fetches the a course using the id
 * @returns {Promise} class - a promise of course to be resolved.
 */
export function getCourse(id, userId) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes/${id}?userId=${userId}`);
	}
	return Promise.resolve(new Response({
		message: 'Unauthorised'
	}, {
		status: 403, statusText: 'Unauthorised'
	}));
}

/**
 * Fetches a course forum using the id of the course
 * @returns {Promise} class - a promise of course to be resolved.
 */
export function getCourseDiscussion(id) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes/${id}/forum`);
	}
	return Promise.resolve(new Response({
		message: 'Unauthorised'
	}, {
		status: 403, statusText: 'Unauthorised'
	}));
}

/**
 * Create a course forum
 * @param {Number} id - id of course forum
 * @param {Object} data - object with the content of forum
 * @returns {Promise} a promise of the course forum to be created
 */
export function createCourseForum(id, data) {
	if (isLoggedIn()) {
		return axios.post(`/v1/classes/${id}/forum`, data);
	}
	return Promise.resolve(new Response({
		message: 'Unauthorised'
	}, {
		status: 403, statusText: 'Unauthorised'
	}));
}

/**
 * Fetches a course module using the id of the course
 * @returns {Promise} class - a promise of course module to be resolved.
 */
export function getLesson(onlineClassId, id, userId) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes/${onlineClassId}/lessons/${id}?userId=${userId}`);
	}
	return Promise.resolve(new Response({
		message: 'Unauthorised'
	}, {
		status: 403, statusText: 'Unauthorised'
	}));
}

/**
 * Fetches a course module using the id of the course
 * @returns {Promise} class - a promise of course module to be resolved.
 */
export function getLessonModule(onlineClassId, id, moduleId, userId) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes/${onlineClassId}/lessons/${id}/modules/${moduleId}?userId=${userId}`);
	}
	return Promise.resolve(new Response({
		message: 'Unauthorised'
	}, {
		status: 403, statusText: 'Unauthorised'
	}));
}

/**
 * Allows a student to join a course
 * @param {Number} id - id of the student
 */
export function joinCourse(id, data) {
	if (isLoggedIn()) {
		return axios.post(`/v1/classes/${id}/learners`, data);
	}
	return Promise.resolve(new Response({
		message: 'Unauthorised'
	}, {
		status: 403, statusText: 'Unauthorised'
	}));
}

export function getDiscussionThread(id, discussionId) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes/${id}/forum/${discussionId}`)
			.then(response => response);
	}
	return Promise.resolve(new Response(
		{ message: 'Unauthorised' },
		{ status: 403, statusText: 'Unauthorised' }
	));
}

/**
 * Creates a new forum thread
 * @param {Number} id - id of forum topic
 * @param {Object} data - object with the content of thread
 * @returns {Promise} a promise of forum topic to be created
 */
export function createDiscussionThread(id, discussionId, data) {
	if (isLoggedIn()) {
		return axios.post(`/v1/classes/${id}/forum/${discussionId}/thread`, data);
	}
	return Promise.resolve(new Response(
		{ message: 'Unauthorised' },
		{ status: 403, statusText: 'Unauthorised' }
	));
}

/**
 * Creates a new forum thread
 * @param {Number} id - id of course
 * @returns {Promise} a promise of forum topic to be created
 */
export function getCourseQuiz(id) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes/${id}/quiz`);
	}
	return Promise.resolve(new Response(
		{ message: 'Unauthorised' },
		{ status: 403, statusText: 'Unauthorised' }
	));
}

/**
 * Deletes a course discussion
 * @param {Number} id  - id of course
 * @param {Number} discussionId - id of discussion
 * @returns {Promise} a promise of deleted course discussion
 */
export function deleteCourseForum(id, discussionId) {
	if (isLoggedIn()) {
		return axios.delete(`/v1/classes/${id}/forum/${discussionId}`);
	}
	return Promise.resolve(new Response(
		{ message: 'Unauthorised' },
		{ status: 403, statusText: 'Unauthorised' }
	));
}

/**
 * Lists discussion thread along with users
 * @param {Number} id - id of course
 * @param {Number} discussionId - id of course discussion
 * @returns {Promise} a promise of discussions and users
 */
export function getDiscussionThreadList(id, discussionId) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes/${id}/forum/${discussionId}/thread`)
			.then(response => response);
	}
	return Promise.resolve(new Response(
		{ message: 'Unauthorised' },
		{ status: 403, statusText: 'Unauthorised' }
	));
}

/**
 * Deletes a course discussion
 * @param {Number} id  - id of course
 * @param {Number} discussionId - id of discussion
 * @returns {Promise} a promise of deleted course discussion
 */
export function deleteCourseDiscussionThread(id, discussionId, threadId) {
	if (isLoggedIn()) {
		return axios.delete(`/v1/classes/${id}/forum/${discussionId}/thread/${threadId}`);
	}
	return Promise.resolve(new Response(
		{ message: 'Unauthorised' },
		{ status: 403, statusText: 'Unauthorised' }
	));
}

/**
 * Get list of course learners
 * @param {Number} classId  - id of course
 * @param {Number} userId - id of user
 * @returns {Promise} a promise of deleted course discussion
 */
export function getCourseLearner(classId, userId) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes/${classId}/learners/${userId}`);
	}
	return Promise.resolve(new Response({
		message: 'Unauthorised'
	}, {
		status: 403, statusText: 'Unauthorised'
	}));
}

/**
 * Get no of attempts of user has taken a course
 * @param {Number} classId  - id of course
 * @param {Number} userId - id of user
 * @returns {Promise} a promise of deleted course discussion
 */
export function getUserCourseAttempts(classId, userId) {
	if (isLoggedIn()) {
		return axios.get(`/v1/classes/${classId}/user/${userId}`);
	}
	return Promise.resolve(new Response({
		message: 'Unauthorised'
	}, {
		status: 403, statusText: 'Unauthorised'
	}));
}